import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import {
  useFetchFlagCount,
  useFetchKeywordsCount,
  useFetchTopicsCount,
  useGetItemSources,
} from "../../api/ItemsApi";

import { ReportSummaryItemApi } from "../../api/Reports";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import InputField from "../../shared-components/InputField";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";
import { SidebarBehaviors } from "./SidebarBehaviors";
import { SidebarKeywords } from "./SidebarKeywords";
import { CandidateInfoList } from "./SidebarPersonInfo";
import { classNames, LONG_TEXT_WRAP_CLASSES } from "../../utils";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import ViewReportLogsButton from "../../shared-components/ViewReportLogsButton";
import EuRegionBadge from "../../shared-components/EuRegionBadge";
import { FlagCountResponse, GroupCount, ItemSource, TopicCount } from "../../api/Items";
import BuildingOutlineIcon from "../../shared-components/icons/BuildingOutlineIcon";
import CheckBadgeIcon from "../../shared-components/icons/CheckBadgeIcon";
import CopyIcon from "../../shared-components/CopyIcon";
import { useKeywordGroupsForBehaviorKit } from "../../api/BlueprintsApi";
import { SidebarTopics } from "./SidebarTopics";
import { countByName, displayedCount, FilterCount } from "./sidebarFilterCount";

export enum SummaryFilterTypeEnum {
  date = "date",
  source = "source",
  postType = "postType",
  behavior = "behavior",
  itemType = "itemType",
  keyword = "keyword",
  topic = "topic",
  reset = "reset",
}

const allowedItemTypes: { [key: string]: string } = {
  social_media_post: "Posts",
  web_content: "Articles",
};
const SourcesSkiped = ["PDFGenerator"];

type Props = {
  report: ReportSummaryItemApi;
  showReportLog: () => void;
  errorBlueprint: boolean;
  checkTypeColor: string;
  hideItemTypeFilter?: boolean;
  applyFilterByType: (
    type: SummaryFilterTypeEnum,
    event: React.ChangeEvent<HTMLInputElement>,
    source: string
  ) => void;
  dateFilter: { fromDate: string; toDate: string };
  summaryOnly?: boolean;
  lookbackMonths?: number;
  keywordFilter: string[];
  topicFilter: string[];
  filtersActive?: boolean;
  filteredFlagCount?: FlagCountResponse;
  filteredKeywordCount?: GroupCount;
  filteredTopicCount?: TopicCount;
};

const truncateText = (text: string, maxLength: number = 24): string => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}..`;
};

const formatLookbackPeriod = (months: number | undefined): string => {
  if (months === undefined || months === null) return "N/A";
  return `${months} ${months === 1 ? "Month" : "Months"}`;
};

type SourceNameDisplayMap = {
  [key: string]: string;
};

const SidebarSummary = ({
  report,
  showReportLog,
  applyFilterByType,
  errorBlueprint,
  checkTypeColor,
  hideItemTypeFilter = false,
  dateFilter,
  summaryOnly = false,
  lookbackMonths,
  keywordFilter,
  topicFilter,
  filtersActive = false,
  filteredFlagCount,
  filteredKeywordCount,
  filteredTopicCount,
}: Props) => {
  const showFlagTotals = filtersActive && !!filteredFlagCount;
  const [showSourceFilter, setShowSourceFilter] = useState(true);
  const [showBehaviorsFilter, setShowBehaviorsFilter] = useState(true);
  const [showKeywordFilter, setShowKeywordFilter] = useState(true);
  const [showTopicFilter, setShowTopicFilter] = useState(true);
  const [showPostTypeFilter, setShowPostTypeFilter] = useState(true);
  const [showItemTypeFilter, setShowItemTypeFilter] = useState(true);
  const [showDateFilter, setShowDateFilter] = useState(true);

  const [itemTypesOptions, setItemTypesOptions] = useState<string[]>([]);
  const [itemSourcesOptions, setitemSourcesOptions] = useState<string[]>([]);
  const [postTypesOptions, setPostTypesOptions] = useState<string[]>([]);

  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");

  const { canAddNotes } = usePermissionValidator();
  const { data: flagCount, mutate: getFlags } = useFetchFlagCount();

  const { data: keywordCount, mutate: getKeywordCount } =
    useFetchKeywordsCount();

  const { data: topicCount, mutate: getTopicCount } = useFetchTopicsCount();

  // Unfiltered on purpose: these counts are the filter *options*, so selecting one
  // facet must not remove the others from the sidebar.
  useEffect(() => {
    if (!report) return;
    getFlags({ report_uuid: report.report_uuid });
    getKeywordCount({ report_uuid: report.report_uuid });
    getTopicCount({ report_uuid: report.report_uuid });
  }, [getFlags, getKeywordCount, getTopicCount, report]);

  const { data: keywordGroups } = useKeywordGroupsForBehaviorKit(
    report.behavior_kit_uuid
  );

  const [itemSources, setItemSources] = useState<ItemSource[]>([]);
  const [sourceNameDisplay, setSourceNameDisplay] =
    useState<SourceNameDisplayMap>({});
  const {
    data: itemSourcesData,
    mutate: itemSourcesGet,
    isLoading: itemSourcesLoading,
    isError: itemSourcesError,
  } = useGetItemSources();

  useEffect(() => {
    const storedData = sessionStorage.getItem("itemSources");
    if (storedData) {
      setItemSources(JSON.parse(storedData));
    } else {
      if (!itemSourcesLoading && !itemSourcesError && !itemSourcesData) {
        itemSourcesGet();
      }
    }
  }, [
    itemSourcesGet,
    itemSourcesLoading,
    itemSourcesError,
    itemSourcesData,
    setItemSources,
  ]);

  useEffect(() => {
    if (itemSourcesData && itemSourcesData.length > 0) {
      setItemSources(itemSourcesData);
      sessionStorage.setItem("itemSources", JSON.stringify(itemSourcesData));
    }
  }, [itemSourcesData, setItemSources]);

  useEffect(() => {
    if (itemSources && itemSources.length > 0) {
      const nameMapping: SourceNameDisplayMap = {};
      for (const itemSource of itemSources) {
        nameMapping[itemSource.name.toLocaleLowerCase()] =
          itemSource.display || itemSource.name;
      }
      setSourceNameDisplay(nameMapping);
    }
  }, [itemSources, setSourceNameDisplay]);

  useEffect(() => {
    setFromDate(dateFilter.fromDate || "");
    setToDate(dateFilter.toDate || "");
  }, [dateFilter]);

  useEffect(() => {
    if (flagCount) {
      const newItemTypes: string[] = flagCount?.item_types?.groups
        .filter((itemType) => {
          return itemType.name in allowedItemTypes;
        })
        .map((itemType) => allowedItemTypes[itemType.name]);
      setItemTypesOptions(newItemTypes);

      const newSources: string[] = flagCount?.item_sources?.groups
        .filter((source) => !SourcesSkiped.includes(source.name))
        .map((source) => source.name);
      let itemSources = new Set(newSources); // even if a source like twitter is added more than 1 time in a blueprint it will be shown once
      setitemSourcesOptions(Array.from(itemSources));

      const newPosts: string[] = flagCount?.post_types?.groups.map(
        (post) => post.name
      );
      let postTypes = new Set(newPosts);
      setPostTypesOptions(Array.from(postTypes));
    }
  }, [flagCount]);

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    if (name === "fromDate") setFromDate(e?.target?.value);
    else if (name === "toDate") setToDate(e?.target?.value);
    applyFilterByType(SummaryFilterTypeEnum.date, e, name);
  };

  return (
    <div className="mb-3">
      <div className="flex flex-nowrap flex-col">
        {!summaryOnly && (
          <>
            {/* Header Section - Name, Company, Check Type */}
            <div className="p-4">
              {/* Primary Name */}
              <div
                className={classNames(
                  "text-xl leading-7 font-bold mb-3",
                  LONG_TEXT_WRAP_CLASSES
                )}
              >
                {report.full_name}
                <EuRegionBadge
                  personUuid={report.person_uuid}
                  className="ml-2"
                />
              </div>

              {/* Company with icon */}
              <div className="flex items-start gap-2 mb-2 text-sm text-gray-700 min-w-0">
                <span className="flex-shrink-0 mt-0.5 inline-flex" aria-hidden>
                  <BuildingOutlineIcon />
                </span>
                <span className={LONG_TEXT_WRAP_CLASSES}>
                  {report.parent_company_name && (
                    <>{report.parent_company_name} | </>
                  )}
                  {report.company_name ?? "N/A"}
                </span>
              </div>

              {/* Check Type with icon and color */}
              <div className="flex items-start gap-2 mb-4 text-sm min-w-0">
                <CheckBadgeIcon
                  className={classNames(checkTypeColor, "flex-shrink-0 mt-0.5")}
                />
                <span
                  className={classNames(
                    "font-medium",
                    checkTypeColor,
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {report.blueprint_name}
                </span>
              </div>

              {/* View Report Logs Button - only for users with report:note permission */}
              <ViewReportLogsButton
                onClick={showReportLog}
                canAddNotes={canAddNotes}
              />
            </div>

            {/* Report Info Banner */}
            <div className="text-base bg-gray-50 p-4 font-semibold">
              Report Info
            </div>

            {/* Behavior Kit */}
            <div className="p-4">
              <div className="mb-3 text-sm border-t border-gray-200 pt-2">
                <span className="block text-gray-500 mb-0.5">
                  Behavior Kit:
                </span>
                <span className="font-semibold text-gray-600">
                  {report?.behavior_kit_name ?? "General"}
                </span>
              </div>

              {/* Lookback */}
              {lookbackMonths !== undefined && (
                <div className="mb-3 text-sm border-t border-gray-200 pt-2">
                  <span className="block text-gray-500 mb-0.5">Lookback:</span>
                  <span className="font-semibold text-gray-600">
                    {formatLookbackPeriod(lookbackMonths)}
                  </span>
                </div>
              )}

              {/* Report ID - truncated with copy */}
              <div className="mb-3 text-sm border-t border-gray-200 pt-2">
                <span className="block text-gray-500 mb-0.5">Report ID:</span>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">
                    {truncateText(report.report_uuid)}
                  </span>
                  <CopyIcon textToCopy={report.report_uuid} />
                </div>
              </div>

              {/* Client Ref ID - truncated with copy (only if exists) */}
              {report.client_report_id && (
                <div className="mb-3 text-sm border-t border-gray-200 pt-2">
                  <span className="block text-gray-500 mb-0.5">
                    Client Ref ID:
                  </span>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-600">
                      {truncateText(report.client_report_id)}
                    </span>
                    <CopyIcon textToCopy={report.client_report_id} />
                  </div>
                </div>
              )}
            </div>

            {/* Person Info Banner */}
            <div className="mt-2">
              <div className="text-base bg-gray-50 p-4 font-semibold">
                Person Info
              </div>
              <div className="p-4">
                <CandidateInfoList report={report} />
              </div>
            </div>

            {errorBlueprint && (
              <ErrorMessageContainer message="Error loading check type" />
            )}
          </>
        )}

        <div>
          <div className="text-lg bg-gray-50 leading-5 font-semibold p-4 mb-4 rounded-md">
            Filters:
          </div>
          <div className="flex flex-nowrap flex-col py-2">
            <div
              className="flex justify-between "
              onClick={() => {
                setShowSourceFilter(!showSourceFilter);
              }}
            >
              <div className="text-md font-semibold mb-2 ml-4">Source:</div>
              <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                {showSourceFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>

            <div
              className={classNames(
                "flex flex-nowarp flex-col",
                showSourceFilter ? "" : "hidden"
              )}
            >
              <div>
                {itemSourcesOptions
                  .sort()
                  .map((source: string, idx: number) => {
                    let displayName: string = source;
                    const counts = displayedCount(
                      countByName(flagCount?.item_sources.groups, source) ?? 0,
                      countByName(
                        filteredFlagCount?.item_sources.groups,
                        source
                      ),
                      showFlagTotals
                    );
                    displayName =
                      sourceNameDisplay[displayName.toLocaleLowerCase()] ??
                      displayName;
                    return (
                      <div
                        key={`source-${source}-${idx}`}
                        className="flex gap-1 text-sm ml-6 mb-1"
                      >
                        <LabeledCheckbox
                          key={source + idx}
                          name={source}
                          onChange={(e) =>
                            applyFilterByType(
                              SummaryFilterTypeEnum.source,
                              e,
                              source
                            )
                          }
                        ></LabeledCheckbox>
                        {displayName}{" "}
                        <FilterCount
                          current={counts.current}
                          total={counts.total}
                          showTotal={counts.showTotal}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
          {!hideItemTypeFilter && (
            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between "
                onClick={() => {
                  setShowItemTypeFilter(!showItemTypeFilter);
                }}
              >
                <div className="text-md font-semibold mb-2 ml-4">
                  Item Type:
                </div>
                <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                  {showItemTypeFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </div>
              </div>

              <div
                className={classNames(
                  "flex flex-nowarp flex-col",
                  showItemTypeFilter ? "" : "hidden"
                )}
              >
                <div>
                  {itemTypesOptions
                    .sort()
                    .map((itemType: string, idx: number) => {
                      const internalName = Object.keys(allowedItemTypes).find(
                        (key) => allowedItemTypes[key] === itemType
                      );
                      const counts = displayedCount(
                        countByName(
                          flagCount?.item_types.groups,
                          internalName ?? ""
                        ) ?? 0,
                        countByName(
                          filteredFlagCount?.item_types.groups,
                          internalName ?? ""
                        ),
                        showFlagTotals
                      );
                      return (
                        <div
                          key={`itemType-${itemType}-${idx}`}
                          className="flex gap-1 text-sm ml-6 mb-1"
                        >
                          <LabeledCheckbox
                            key={itemType + idx}
                            name={itemType}
                            onChange={(e) =>
                              applyFilterByType(
                                SummaryFilterTypeEnum.itemType,
                                e,
                                internalName ?? ""
                              )
                            }
                          ></LabeledCheckbox>
                          {itemType}{" "}
                          <FilterCount
                            current={counts.current}
                            total={counts.total}
                            showTotal={counts.showTotal}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-nowrap flex-col py-2">
            <div
              className="flex justify-between "
              onClick={() => {
                setShowPostTypeFilter(!showPostTypeFilter);
              }}
            >
              <div className="text-md font-semibold mb-2 ml-4">Post Type:</div>
              <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                {showPostTypeFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>

            <div
              className={classNames(
                "flex flex-nowarp flex-col ml-6",
                showPostTypeFilter ? "" : "hidden"
              )}
            >
              <div>
                {postTypesOptions.sort().map((source: string, idx: number) => {
                  const counts = displayedCount(
                    countByName(flagCount?.post_types.groups, source) ?? 0,
                    countByName(filteredFlagCount?.post_types.groups, source),
                    showFlagTotals
                  );
                  return (
                    <div
                      key={`postType-${source}-${idx}`}
                      className="flex text-sm gap-1 mb-1"
                    >
                      <LabeledCheckbox
                        key={source + idx}
                        name={source}
                        onChange={(e) =>
                          applyFilterByType(
                            SummaryFilterTypeEnum.postType,
                            e,
                            source.toLowerCase()
                          )
                        }
                      ></LabeledCheckbox>
                      {source}{" "}
                      <FilterCount
                        current={counts.current}
                        total={counts.total}
                        showTotal={counts.showTotal}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex flex-nowrap flex-col py-2">
            <div
              className="flex justify-between "
              onClick={() => {
                setShowBehaviorsFilter(!showBehaviorsFilter);
              }}
            >
              <div className="text-md font-semibold mb-2 ml-4">Behaviors:</div>
              <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                {showBehaviorsFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>

            <div
              className={classNames(
                "flex flex-nowarp flex-col ml-6",
                showBehaviorsFilter ? "" : "hidden"
              )}
            >
              <SidebarBehaviors
                flagCount={flagCount}
                filteredFlagCount={filteredFlagCount}
                filtersActive={filtersActive}
                applyFilterByType={applyFilterByType}
                hasCheckbox={true}
              />
            </div>
          </div>

          <div className="flex flex-nowrap flex-col py-2">
            <div
              className="flex justify-between "
              onClick={() => {
                setShowKeywordFilter(!showKeywordFilter);
              }}
            >
              <div className="text-md font-semibold mb-2 ml-4">Keywords:</div>
              <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                {showKeywordFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>
            <div
              className={classNames(
                "flex flex-nowarp flex-col ml-6",
                showKeywordFilter ? "" : "hidden"
              )}
            >
              <SidebarKeywords
                keywordCount={keywordCount}
                filteredKeywordCount={filteredKeywordCount}
                filtersActive={filtersActive}
                keywordGroups={keywordGroups}
                applyFilterByType={applyFilterByType}
                hasCheckbox={true}
                selections={keywordFilter}
              />
            </div>
          </div>

          {(topicCount?.groups?.length ?? 0) > 0 && (
            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between"
                onClick={() => setShowTopicFilter(!showTopicFilter)}
              >
                <div className="text-md font-semibold mb-2 ml-4">Topics:</div>
                <div className="w-5 mr-4 py-1 text-gray-400">
                  {showTopicFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </div>
              </div>
              <div
                className={classNames(
                  "flex flex-nowrap flex-col ml-6",
                  showTopicFilter ? "" : "hidden"
                )}
              >
                <SidebarTopics
                  topicCount={topicCount}
                  filteredTopicCount={filteredTopicCount}
                  filtersActive={filtersActive}
                  selections={topicFilter}
                  applyFilterByType={applyFilterByType}
                />
              </div>
            </div>
          )}

          <div className="flex flex-nowrap flex-col py-2">
            <div
              className="flex justify-between"
              onClick={() => {
                setShowDateFilter(!showDateFilter);
              }}
            >
              <div className="text-md font-semibold mb-2 ml-4">Date Range:</div>
              <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                {showDateFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>
            {showDateFilter && (
              <div className="flex flex-nowrap flex-col px-4">
                <div className="flex flex-row mb-2">
                  <InputField
                    handleChange={(e) => {
                      handleDateChange(e, "fromDate");
                    }}
                    name={"from-date"}
                    placeholder=""
                    label={"From Date"}
                    type="date"
                    max={toDate}
                    classes={"w-full"}
                    value={fromDate}
                  />
                </div>
                <div className="flex flex-row">
                  <InputField
                    handleChange={(e) => {
                      handleDateChange(e, "toDate");
                    }}
                    name={"to-date"}
                    placeholder=""
                    label={"To Date"}
                    type="date"
                    min={fromDate}
                    classes={"w-full"}
                    value={toDate}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidebarSummary;
